import React from "react";
import { Block, Span, parse } from "./markdown";

/**
 * Formatted text, rendered as React elements (C12).
 *
 * **There is no `dangerouslySetInnerHTML` here and there must never be one.**
 * The stored answer is markdown; this builds elements from the parsed blocks,
 * so nothing a person types can become markup. That is the property that lets a
 * rich-text field exist in this product with no sanitiser on either side of the
 * wire -- see `main/richtext.py` for the whole argument.
 *
 * Used three ways from one component: the live preview beside the editor, the
 * read-only rendering of an answer on a submitted permit (`mapReadPretty`), and
 * the static guidance block an administrator writes into a form.
 */

const renderSpan = (span: Span, key: number): React.ReactNode => {
  if (span.href) {
    // `noopener` because the address came from a form answer somebody typed:
    // without it the opened page can reach back through `window.opener`.
    // `nofollow` because a permit is not an endorsement.
    return (
      <a key={key} href={span.href} target="_blank" rel="noopener noreferrer nofollow">
        {span.text}
      </a>
    );
  }
  let node: React.ReactNode = span.text;
  if (span.code)
    node = (
      <code
        style={{
          fontFamily: "Consolas, Menlo, monospace",
          background: "rgba(0,0,0,.05)",
          padding: "1px 4px",
          borderRadius: 3,
        }}
      >
        {node}
      </code>
    );
  if (span.italic) node = <em>{node}</em>;
  if (span.bold) node = <strong>{node}</strong>;
  return <React.Fragment key={key}>{node}</React.Fragment>;
};

const spans = (block: Block) => block.spans.map(renderSpan);

const HEADING_SIZE: Record<number, number> = { 1: 17, 2: 15, 3: 14 };

/**
 * Consecutive list lines are gathered into one `<ul>`/`<ol>`.
 *
 * The parser is deliberately line-at-a-time (it has to be: the Python side
 * builds one Word paragraph per line), so grouping is the renderer's job. Left
 * ungrouped, every bullet becomes its own single-item list and the browser
 * spaces them like paragraphs -- which does not look like a list.
 */
export const MarkdownView: React.FC<{ value?: string | null; style?: React.CSSProperties }> = ({
  value,
  style,
}) => {
  const blocks = parse(value);
  const out: React.ReactNode[] = [];
  let list: { kind: "bullet" | "number"; items: Block[] } | null = null;

  const closeList = () => {
    if (!list) return;
    const items = list.items.map((block, i) => <li key={i}>{spans(block)}</li>);
    out.push(
      list.kind === "bullet" ? (
        <ul key={out.length} style={{ margin: "4px 0", paddingLeft: 22 }}>
          {items}
        </ul>
      ) : (
        <ol key={out.length} style={{ margin: "4px 0", paddingLeft: 22 }}>
          {items}
        </ol>
      )
    );
    list = null;
  };

  blocks.forEach((block, index) => {
    if (block.kind === "bullet" || block.kind === "number") {
      if (list && list.kind !== block.kind) closeList();
      if (!list) list = { kind: block.kind, items: [] };
      list.items.push(block);
      return;
    }
    closeList();
    if (block.kind === "heading") {
      out.push(
        <div
          key={index}
          style={{
            fontSize: HEADING_SIZE[block.level] ?? 14,
            fontWeight: 600,
            margin: "10px 0 4px",
          }}
        >
          {spans(block)}
        </div>
      );
      return;
    }
    if (!block.spans.length) {
      // A blank line the person typed. Rendered as space rather than dropped:
      // collapsing it is an edit to their answer.
      out.push(<div key={index} style={{ height: 8 }} />);
      return;
    }
    out.push(
      <p key={index} style={{ margin: "0 0 6px" }}>
        {spans(block)}
      </p>
    );
  });
  closeList();

  return (
    <div style={{ lineHeight: 1.6, wordBreak: "break-word", ...style }}>{out}</div>
  );
};

export default MarkdownView;
